// newline을 <br>로 변환
import { Fragment } from 'react';
import { stringify } from 'qs';

export const nl2br = (str: string) =>
  str
    .trim()
    .split(/\r\n|\r|\n|\\n/)
    .map((item, key) => (
      <Fragment key={key}>
        {key > 0 && <br />}
        {item}
      </Fragment>
    ));

// - 영어 대소문자, 숫자, 일부 특수문자(점(.), 언더바(_), 하이픈(-)) 사용 가능
// - 점(.)은 연속 사용, 맨앞맨뒤 사용 불가
// - 띄어쓰기 포함 불가
export const EMAIL_ID_REGEX = /^(?!.*\.\.)(?!^\.)[A-Za-z0-9_.-]+(?<!\.)$/;

// - 언더바 사용 불가
// - 대신 하이픈(-) 사용 가능
// - 이외 아이디와 동일
export const EMAIL_DOMAIN_REGEX = /^(?!.*\.\.)(?!^\.)[A-Za-z0-9-.]+(?<!\.)$/;

/** 유효한 형식의 이메일인지 여부를 반환 */
export const isValidEmail = ({ id, domain }: { id: string; domain: string }) =>
  EMAIL_ID_REGEX.test(id) && EMAIL_DOMAIN_REGEX.test(domain);

/**
 * 이메일 입력창에 값이 있는지 체크하는 정규식
 * ryan@ = false
 * ryan@kakao = true
 * ryan@kakao.com = true
 */
export const isFullfilledEmail = (email: string) => /.@./.test(email);

/** 주어진 문자열에서 숫자만 필터링 */
export const filterNumberOnly = (v: string) => v.replaceAll(/[^\d]/g, '');

/** 문자열의 길이 검사를 위한 커링 */
export const lengthValidator = (length: number) => (v: string) => v.length === length;

export const getTimeStamp = () => new Date().getTime().toString();

export const getIsDuplicatedString = (stringArr: string[]) => {
  const set = new Set(stringArr);
  return set.size !== stringArr.length;
};

export const getPostposition = (word: string) => {
  const lastChar = word[word.length - 1];
  const lastCharCode = lastChar.charCodeAt(0);

  // 한글 자음 모음 분리
  const isHangul = lastCharCode >= 0xac00 && lastCharCode <= 0xd7a3;

  if (isHangul) {
    const lastCharIndex = (lastCharCode - 0xac00) % 28;

    return lastCharIndex === 0 ? `${word}로` : `${word}으로`;
  }

  return `${word}로`;
};

const camelToSnakeCase = (str: string) => str.replace(/([A-Z])/g, '_$1').toLowerCase();

export const upperSnakeToCamelCase = (str: string): string => {
  return str.toLowerCase().replace(/([-_][a-z])/g, (group) => group.replace('_', '').toUpperCase());
};

export const snakeToCamelCase = (str: string): string => {
  return str.replace(/([-_][a-z])/g, (group) => group.replace('_', '').toUpperCase());
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/** FE -> 서버 */
export const convertKeysToSnakeCase = (queryParams: Record<string, unknown>): Record<string, unknown> => {
  return Object.keys(queryParams).reduce<Record<string, unknown>>((acc, key) => {
    const snakeCaseKey = camelToSnakeCase(key);
    const value = queryParams[key];

    if (isObject(value)) {
      acc[snakeCaseKey] = convertKeysToSnakeCase(value);
      return acc;
    }

    if (Array.isArray(value)) {
      acc[snakeCaseKey] = value.map((item) => (isObject(item) ? convertKeysToSnakeCase(item) : item));
      return acc;
    }

    acc[snakeCaseKey] = value;
    return acc;
  }, {});
};

/** 서버 -> FE */
export const convertKeysToCamelCase = (
  queryParams: unknown[] | Record<string, unknown>
): unknown[] | Record<string, unknown> => {
  // 배열 처리
  if (Array.isArray(queryParams)) {
    return queryParams.map((item) =>
      item && typeof item === 'object' ? convertKeysToCamelCase(item as unknown[]) : item
    );
  }
  // 객체 처리
  return Object.keys(queryParams).reduce<Record<string, unknown>>((acc, key) => {
    const camelCaseKey = snakeToCamelCase(key);
    const value = (queryParams as Record<string, unknown>)[key];

    if (value && typeof value === 'object') {
      acc[camelCaseKey] = convertKeysToCamelCase(value as Record<string, unknown>);
    } else {
      acc[camelCaseKey] = value;
    }

    return acc;
  }, {});
};

export const getQueryParams = ({ params, disable = false }: { params: Record<string, unknown>; disable?: boolean }) => {
  if (disable) return `?${stringify(params, { arrayFormat: 'repeat' })}`;
  return `?${stringify(convertKeysToSnakeCase(params), { arrayFormat: 'repeat' })}`;
};

export const isValidZipCode = (zipCode: string) => {
  const fiveDigitPattern = /^\d{5}$/;
  const threeDigitDashPattern = /^\d{3}-\d{3}$/;

  return Boolean(zipCode.match(fiveDigitPattern)) || Boolean(zipCode.match(threeDigitDashPattern));
};
